import React, { useState } from "react";
import {
  useGetStagingTemplateDetailQuery,
  useGetProdTemplateDetailQuery,
} from "../../../../app/api/console/scanTemplateSyncApiSlice";

const SyncConfirmDialog = ({ templateId, direction, onConfirm, onCancel, isSyncing }) => {
  const { data: stagingDetail } = useGetStagingTemplateDetailQuery({ templateId });
  const { data: prodDetail } = useGetProdTemplateDetailQuery({ templateId });

  const source = direction === "to-prod" ? stagingDetail : prodDetail;
  const target = direction === "to-prod" ? prodDetail : stagingDetail;
  const dirLabel = direction === "to-prod" ? "Staging → Production" : "Production → Staging";

  const changes = [];

  if (source && target) {
    if (source.title !== target.title) changes.push({ field: "Title", from: target.title, to: source.title });
    if (source.scan_mode !== target.scan_mode) changes.push({ field: "Scan Mode", from: target.scan_mode, to: source.scan_mode });
    if (source.plan_type !== target.plan_type) changes.push({ field: "Plan Type", from: target.plan_type, to: source.plan_type });
    if (source.automation_ready !== target.automation_ready) changes.push({ field: "Automation Ready", from: String(target.automation_ready), to: String(source.automation_ready) });

    const sourceBaseText = source.base_prompt?.current_prompt_text || "";
    const targetBaseText = target.base_prompt?.current_prompt_text || "";
    if (sourceBaseText !== targetBaseText) {
      changes.push({ field: "Base Prompt", from: targetBaseText?.slice(0, 100) + (targetBaseText?.length > 100 ? "..." : ""), to: sourceBaseText?.slice(0, 100) + (sourceBaseText?.length > 100 ? "..." : ""), isPrompt: true, fullFrom: targetBaseText, fullTo: sourceBaseText });
    }

    const sourceWP = source.website_prompts || [];
    const targetWP = target.website_prompts || [];
    sourceWP.forEach((sp) => {
      const tp = targetWP.find((p) => p.website_id === sp.website_id);
      if (!tp) {
        changes.push({ field: `Website Prompt (${sp.website_id})`, from: "(does not exist)", to: sp.current_prompt_text?.slice(0, 80) + "..." });
      } else if (sp.current_prompt_text !== tp.current_prompt_text) {
        changes.push({ field: `Website Prompt (${sp.website_id})`, from: tp.current_prompt_text?.slice(0, 80) + "...", to: sp.current_prompt_text?.slice(0, 80) + "..." });
      }
    });

    const sourceTC = source.test_cases_containers || [];
    const targetTC = target.test_cases_containers || [];
    const targetTcByWebsite = {};
    for (const tc of targetTC) { targetTcByWebsite[tc.website_id || "__base__"] = tc; }

    for (const sc of sourceTC) {
      const key = sc.website_id || "__base__";
      const tc = targetTcByWebsite[key];
      const label = sc.website_id ? `Test Cases (${sc.website_id})` : "Test Cases (Base)";
      const sCases = sc.current_test_cases || [];
      const sJson = JSON.stringify(sc.current_test_cases || []);
      if (tc) {
        const tCases = tc.current_test_cases || [];
        const tJson = JSON.stringify(tc.current_test_cases || []);
        if (sJson !== tJson) {
          changes.push({ field: label, from: `${tCases.length} test case${tCases.length !== 1 ? "s" : ""}`, to: `${sCases.length} test case${sCases.length !== 1 ? "s" : ""}` });
        }
      } else {
        changes.push({ field: label, from: "(does not exist)", to: `${sCases.length} test case${sCases.length !== 1 ? "s" : ""}` });
      }
    }
    for (const tc of targetTC) {
      const key = tc.website_id || "__base__";
      if (!sourceTC.some((s) => (s.website_id || "__base__") === key)) {
        const tCases = tc.current_test_cases || [];
        const label = tc.website_id ? `Test Cases (${tc.website_id})` : "Test Cases (Base)";
        changes.push({ field: label, from: `${tCases.length} test case${tCases.length !== 1 ? "s" : ""}`, to: "(will be removed)" });
      }
    }
  } else if (source && !target) {
    changes.push({ field: "Template", from: "(does not exist)", to: source.title });
  }

  const [expandedPrompt, setExpandedPrompt] = useState(null);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 shadow-xl max-h-[85vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Confirm Sync</h3>
        <p className="text-sm text-gray-500 mb-4">{dirLabel}</p>

        {changes.length === 0 ? (
          <div className="py-8 text-center text-gray-500">
            <p className="font-medium">No changes detected</p>
            <p className="text-sm mt-1">Source and target are already in sync.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">{changes.length} change{changes.length !== 1 ? "s" : ""} will be applied:</p>
            {changes.map((change, i) => (
              <div key={i} className="border rounded-lg p-3">
                <div className="text-sm font-medium text-gray-900 mb-1">{change.field}</div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-gray-500">Current:</span>
                    <div className="mt-0.5 p-2 bg-red-50 rounded text-red-800 font-mono whitespace-pre-wrap">
                      {change.from || "(empty)"}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">After sync:</span>
                    <div className="mt-0.5 p-2 bg-green-50 rounded text-green-800 font-mono whitespace-pre-wrap">
                      {change.to || "(empty)"}
                    </div>
                  </div>
                </div>
                {change.isPrompt && (
                  <button
                    onClick={() => setExpandedPrompt(expandedPrompt === i ? null : i)}
                    className="text-xs text-indigo-600 hover:text-indigo-800 mt-2"
                  >
                    {expandedPrompt === i ? "Hide full text" : "Show full text"}
                  </button>
                )}
                {change.isPrompt && expandedPrompt === i && (
                  <div className="grid grid-cols-2 gap-3 mt-2 text-xs">
                    <pre className="p-2 bg-red-50 rounded text-red-800 whitespace-pre-wrap max-h-64 overflow-y-auto">{change.fullFrom || "(empty)"}</pre>
                    <pre className="p-2 bg-green-50 rounded text-green-800 whitespace-pre-wrap max-h-64 overflow-y-auto">{change.fullTo || "(empty)"}</pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-3 justify-end mt-6">
          <button onClick={onCancel} className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isSyncing}
            className={`px-4 py-2 text-white rounded-lg text-sm disabled:opacity-50 ${
              direction === "to-prod" ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {isSyncing ? "Syncing..." : `Confirm ${direction === "to-prod" ? "Push to Prod" : "Pull to Staging"}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SyncConfirmDialog;
